// src/routes/faq.tsx
import { createFileRoute } from '@tanstack/react-router';
import { createRouteConfig } from '../utils/routeGuard';
import { useTranslation } from 'react-i18next';
import { staticI18n } from '../i18n';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

// --- Pre-computed SEO (same pattern as your current page) ---
const seoTitle = staticI18n.t('faq.seo.title');
const seoDescription = staticI18n.t('faq.seo.description');
const seoOgTitle = staticI18n.t('faq.seo.ogTitle');
const seoOgDescription = staticI18n.t('faq.seo.ogDescription');

export const Route = createFileRoute('/faq')({
  component: FAQPage,
  ...createRouteConfig({
    isPublic: true,
    seo: {
      title: seoTitle,
      description: seoDescription,
      ogTitle: seoOgTitle,
      ogDescription: seoOgDescription,
    },
  }),
});

type Contact = { type: string; email: string };
type Question = {
  question: string;
  answer: string; // may contain HTML (links), so we render it safely below
  contactList?: Contact[];
  additionalInfo?: string;
};
type Category = { id: string; title: string; questions: Question[] };

function FAQPage() {
  const { t } = useTranslation();

  const raw = t('faq.categories', { returnObjects: true }) as unknown;
  const categories: Category[] = Array.isArray(raw) ? (raw as Category[]) : [];
  if (!Array.isArray(raw)) {
    console.warn('faq.categories missing or malformed in locale');
  }

  return (
    <div className='bg-white text-gray-900'>
      {/* HERO — matches the new public-page style */}
      <section className='relative overflow-hidden border-b'>
        <div
          className='absolute -top-24 -right-24 h-72 w-72 rounded-full bg-blue-200 blur-3xl opacity-40'
          aria-hidden='true'
        />
        <div className='mx-auto max-w-7xl px-4 py-14 text-center'>
          <h1 className='text-3xl md:text-4xl font-extrabold'>
            {t('faq.title')}
          </h1>
          <p className='mt-2 text-gray-700'>{t('faq.lastUpdated')}</p>
        </div>
      </section>

      {/* CONTENT — card list, fed by localization data */}
      <section className='mx-auto max-w-3xl px-4 py-16 space-y-12' id='faq'>
        {categories.map((category) => (
          <div key={category.id} id={category.id} className='space-y-4'>
            <h2 className='text-xl font-bold'>{category.title}</h2>

            <div className='space-y-4'>
              {category.questions.map((q, idx) => (
                <Card key={`${category.id}-${idx}`} className='shadow-sm'>
                  <CardHeader>
                    <CardTitle className='text-base'>{q.question}</CardTitle>
                  </CardHeader>

                  {/* Answers may include inline links (HTML) from i18n */}
                  <CardContent className='prose prose-sm max-w-none text-gray-700'>
                    <div dangerouslySetInnerHTML={{ __html: q.answer }} />

                    {q.contactList && q.contactList.length > 0 && (
                      <ul className='mt-3 list-disc pl-5'>
                        {q.contactList.map((c, i) => (
                          <li key={i}>
                            <strong>{c.type}:</strong>{' '}
                            <a
                              className='text-blue-600 underline hover:text-blue-800'
                              href={`mailto:${c.email}`}
                            >
                              {c.email}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}

                    {q.additionalInfo && (
                      <p className='mt-3'>{q.additionalInfo}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
